import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, InputLabel, MenuItem, Select, styled } from '@mui/material';

import useGetTrapActivationHistory from 'hooks/Traps/Trap/useGetTrapActivationHistory';
import useScreenSize from 'hooks/Screen/useScreenSize';
import { TrapActivationHistory } from 'api/traps/types';
import TrapHistoryExpandable from 'components/TrapsTables/MultiCatchMouseTraps/TrapHistoryExpandable';
import SetActivationSignalStatusModal from 'pages/DevicesDetail/MCT/Modal/SetActivationSignalStatusModal';
import { useGetMCTColumns } from 'components/DevicesDetail/MCT/useGetMCTColumns';
import FilterTrapHistoryDataTable from 'components/DevicesDetail/MCT/HistoryTable/FilterTrapHistoryDataTable';

interface SRBMTrapHistoryCardProps {
  slug: string;
  type: string;
}
const CustomSelect = styled(Select)({
  '&.MuiInputBase-root': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  '& .MuiSelect-select': {
    padding: '10px 12px',
    paddingRight: '24px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #E0E0E0',
  },
});
const SRBMTrapHistoryCard: React.FC<SRBMTrapHistoryCardProps> = ({ slug, type }) => {
  const filterValues = [
    { value: 'all', label: 'All Activity' },
    { value: 'CONSUMPTION', label: 'Only eating bait' },
    { value: 'ACTIVITY', label: 'Only visit without eating bait' },
  ];
  const today = new Date();
  const lastYear = new Date(today);
  lastYear.setFullYear(today.getFullYear() - 1);
  const [startDate, setStartDate] = useState<string>(lastYear.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(today.toISOString().split('T')[0]);
  const [page, setPage] = useState(1);
  const [ordering, setQueryParam] = useState('');
  const [search, setSearch] = useState('');
  const { data, refetch, isLoading } = useGetTrapActivationHistory(slug || '', type, startDate, endDate);
  const [filteringData, setFilteringData] = useState<TrapActivationHistory[]>(data || []);
  const [filterValue, setFilterValue] = useState<string | number | null>('all');
  const columns = useGetMCTColumns(type);
  const searchFields = ['msg_token', 'order_datetime'];
  const { isNotLargeDesktop } = useScreenSize();
  const { t } = useTranslation();
  useEffect(() => {
    refetch();
  }, [page, search, ordering, refetch]);

  useEffect(() => {
    if (filterValue === 'all') {
      setFilteringData(data!);
    } else {
      setFilteringData(data!.filter(item => item.activity_type === filterValue) || []);
    }
  }, [data, filterValue]);

  const handleFilteringChange = (value: string | number | null) => {
    setFilterValue(value);
  };
  const handleFilterChange = (value: string | number | null) => {
    setFilterValue(value);
    handleFilteringChange!(value);
  };
  return (
    <div className="row">
      <div className="col-12">
        <div className="card shadow-lg">
          <div className="card-header">
            <div className="row align-items-center gy-3">
              <div className="col-lg-8 col-md-12 d-flex flex-wrap gap-2 justify-content-center justify-content-lg-start">
                <input
                  type="date"
                  className="form-control"
                  style={{ width: 'auto' }}
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
                <input
                  type="date"
                  className="form-control"
                  style={{ width: 'auto' }}
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                />
              </div>

              <div className="col-lg-4 col-md-12 d-flex justify-content-center justify-content-lg-end">
                <FormControl sx={{ minWidth: 250 }}>
                  <InputLabel shrink sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Activity Type
                  </InputLabel>
                  <CustomSelect
                    value={filterValue || ''}
                    onChange={event => handleFilterChange(event.target.value as string)}
                    label="Activity Type"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      '&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--vz-purple)',
                      },
                    }}
                  >
                    {filterValues?.map(key => (
                      <MenuItem key={key.value} value={key.value} sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {key.label}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </FormControl>
              </div>
            </div>
          </div>
          <div className="card-body" style={{ minHeight: '570px' }}>
            <FilterTrapHistoryDataTable<TrapActivationHistory>
              columns={columns}
              title={t('MCTDetail.TrapHistoryCard.titleSRBM')}
              titlePerPage={t('MCTDetail.TrapHistoryCard.titlePerPage')}
              data={filteringData || []}
              onChangePage={setPage}
              onChangeFilterParams={setQueryParam}
              onChangeSearch={setSearch}
              searchFields={searchFields}
              isLoading={isLoading}
              expandableRows={isNotLargeDesktop}
              expandableRowsComponent={row => <TrapHistoryExpandable data={row.data} />}
            />
            <SetActivationSignalStatusModal type={type} refetchActivationSignals={refetch} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SRBMTrapHistoryCard;
